import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import type { ContainerBreakpoint, ContainerResponsiveValue } from "./contracts/responsive";
import { resolveGapClasses, type Space } from "./foundations/space";

const COLUMNS_ALIGN_CLASSES = {
  stretch: "items-stretch",
  start: "items-start",
  center: "items-center",
  end: "items-end",
} as const;

const COLUMNS_ROW_CLASSES: Record<ContainerBreakpoint, string> = {
  xs: "@xs:flex-row",
  sm: "@sm:flex-row",
  md: "@md:flex-row",
  lg: "@lg:flex-row",
  xl: "@xl:flex-row",
};

const COLUMNS_REVERSE_ROW_CLASSES: Record<ContainerBreakpoint, string> = {
  xs: "@xs:flex-row-reverse",
  sm: "@sm:flex-row-reverse",
  md: "@md:flex-row-reverse",
  lg: "@lg:flex-row-reverse",
  xl: "@xl:flex-row-reverse",
};

type ColumnsElement = "div" | "ul" | "ol";
export type ColumnsAlign = keyof typeof COLUMNS_ALIGN_CLASSES;

export type ColumnsProps<TElement extends ColumnsElement = "div"> = {
  children: ReactNode;
  gap?: ContainerResponsiveValue<Space>;
  align?: ColumnsAlign;
  collapseBelow?: ContainerBreakpoint;
  reverse?: boolean;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

/** Non-wrapping columns. collapseBelow resolves against an explicit Container ancestor. */
export function Columns<TElement extends ColumnsElement = "div">({
  align = "stretch",
  as,
  children,
  collapseBelow,
  gap,
  ref,
  reverse = false,
  ...nativeProps
}: ColumnsProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;
  let directionClass = reverse ? "flex-row-reverse" : "flex-row";
  if (collapseBelow) {
    directionClass = twJoin(
      "flex-col",
      reverse ? COLUMNS_REVERSE_ROW_CLASSES[collapseBelow] : COLUMNS_ROW_CLASSES[collapseBelow],
    );
  }

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin("flex", directionClass, COLUMNS_ALIGN_CLASSES[align], resolveGapClasses(gap))}
    >
      {children}
    </Element>
  );
}

const COLUMN_WIDTH_CLASSES = {
  fluid: "min-inline-size-zero flex-1",
  content: "w-fit shrink-0",
  "one-fifth": "min-inline-size-zero basis-1/5",
  "two-fifths": "min-inline-size-zero basis-2/5",
  "three-fifths": "min-inline-size-zero basis-3/5",
  "four-fifths": "min-inline-size-zero basis-4/5",
  quarter: "min-inline-size-zero basis-1/4",
  half: "min-inline-size-zero basis-1/2",
  "three-quarters": "min-inline-size-zero basis-3/4",
  third: "min-inline-size-zero basis-1/3",
  "two-thirds": "min-inline-size-zero basis-2/3",
} as const;

type ColumnElement = "div" | "li" | "section" | "aside";
export type ColumnWidth = keyof typeof COLUMN_WIDTH_CLASSES;

export type ColumnProps<TElement extends ColumnElement = "div"> = {
  children: ReactNode;
  width?: ColumnWidth;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

export function Column<TElement extends ColumnElement = "div">({
  as,
  children,
  ref,
  width = "fluid",
  ...nativeProps
}: ColumnProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element {...nativeProps} ref={ref} className={COLUMN_WIDTH_CLASSES[width]}>
      {children}
    </Element>
  );
}
